/**
 * Service Worker Registration
 * Implements Requirements 13.3, 13.4
 * 
 * Handles service worker registration and lifecycle management
 */

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

/**
 * Register the service worker
 */
export function register(config?: ServiceWorkerConfig): void {
  if ('serviceWorker' in navigator) {
    // Wait for the page to load before registering
    window.addEventListener('load', () => {
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;

      registerValidSW(swUrl, config);
      
      // Set up online/offline event listeners
      setupNetworkListeners(config);
    });
  }
}

/**
 * Register a valid service worker
 */
function registerValidSW(swUrl: string, config?: ServiceWorkerConfig): void {
  navigator.serviceWorker
    // Update path (ticket FIX-SERVICE-WORKER-UPDATE-PATH-TRANSLAYSHUN, 6 Oct 2026):
    // GitHub Pages serves every file with cache-control max-age=600 and cannot set
    // custom response headers (no no-cache for the worker, no Service-Worker-Allowed).
    // The fix lives in code instead:
    //  - updateViaCache 'none' makes every update check fetch the worker script
    //    straight from the network, never from the 10-minute HTTP cache, so a new
    //    deploy is picked up on the next visit without waiting out max-age=600;
    //  - the worker itself is version-stamped (SW_VERSION) with the standard
    //    skipWaiting()/clients.claim() takeover path (frontend/public/service-worker.js).
    // The worker sits at the site root, so scope '/' is its default maximum scope
    // and no Service-Worker-Allowed header is needed.
    .register(swUrl, { scope: '/', updateViaCache: 'none' })
    .then((registration) => {
      console.log('[SW] Service Worker registered:', registration, {
        scope: registration.scope,
        updateViaCache: registration.updateViaCache,
      });

      // Check for updates periodically
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000); // Check every hour

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is available
              console.log('[SW] New content available; please refresh.');

              if (config?.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content is cached for offline use
              console.log('[SW] Content cached for offline use.');

              if (config?.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[SW] Service Worker registration failed:', error);
    });
}

/**
 * Set up network status listeners
 */
function setupNetworkListeners(config?: ServiceWorkerConfig): void {
  window.addEventListener('online', () => {
    console.log('[Network] Back online');
    if (config?.onOnline) {
      config.onOnline();
    }
  });

  window.addEventListener('offline', () => {
    console.log('[Network] Gone offline');
    if (config?.onOffline) {
      config.onOffline();
    }
  });
}

/**
 * Unregister the service worker
 */
export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('[SW] Service Worker unregistered');
      })
      .catch((error) => {
        console.error('[SW] Service Worker unregistration failed:', error);
      });
  }
}

/**
 * Check if the app is running in offline mode
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

/**
 * Check if service worker is supported
 */
export function isSupported(): boolean {
  return 'serviceWorker' in navigator;
}

/**
 * Get the current service worker registration
 */
export async function getRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready;
  }
  return undefined;
}

/**
 * Send a message to the service worker
 */
export async function sendMessage(message: { type: string; [key: string]: any }): Promise<void> {
  const registration = await getRegistration();
  if (registration?.active) {
    registration.active.postMessage(message);
  }
}

/**
 * Clear all service worker caches
 */
export async function clearCaches(): Promise<void> {
  await sendMessage({ type: 'CLEAR_CACHE' });
}

/**
 * Force service worker to skip waiting and activate
 */
export async function skipWaiting(): Promise<void> {
  await sendMessage({ type: 'SKIP_WAITING' });
}
